import React, { useEffect } from 'react';
import type { FC } from 'react';
import styles from './SecurityGroups.module.scss';
import type { Control } from 'react-hook-form';
import type { SecurityGroupForm } from '../../models/SecurityGroups';
import type { DepartmentMembers } from '../../models/SecurityListMembers';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, Checkbox, TableBody } from '@mui/material';
import { Box } from '@mui/system';
import { scrollStyles, defaultCheckboxStyles, headerHeight, tabHeaderHeight } from '../../theme/styles';
import { ButtonRowHeight } from './Index';
import { useTranslation, type UseTranslationResponse } from 'react-i18next';

export interface GroupsTableProps {
  control?: Control<SecurityGroupForm>
  groupID?: string
  members: DepartmentMembers
  selected: string[]
  setSelected: (selected: string[]) => void
}

interface DisplayMember {
  departmentName: string
  position: string
  firstName: string
  lastName: string
  personId: string
}

const GroupsTable: FC<GroupsTableProps> = ({ control, groupID, members, selected, setSelected }) => {
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [flatMembersList, setFlatMembersList] = React.useState<DisplayMember[]>([]);
  const [isAllSelected, setAllSelected] = React.useState<boolean>(false);

  const { t }: UseTranslationResponse<'translation', undefined> = useTranslation();

  useEffect(() => {
    const flatMembers = [];

    for (const key of Object.keys(members)) {
      const items = members[key];

      for (const item of items) {
        flatMembers.push({
          departmentName: key,
          position: item.jobTitle,
          firstName: item.firstName,
          lastName: item.lastName,
          personId: item.personId
        });
      }
    }

    setFlatMembersList(flatMembers);
  }, [members])

  const handleSetExpanded = (newExpanded: string[]): void => {
    setExpanded(newExpanded);
  }

  const isSelected = (name: string) => selected.includes(name);

  const selectAll = () => {
    const newAllSelected = !isAllSelected;
    if (newAllSelected) {
      setSelected(flatMembersList.map(item => item.personId));
    } else {
      setSelected([]);
    }

    setAllSelected(newAllSelected);
  }

  function GroupedTableRows (props: { department: string, visibleMembers: DisplayMember[], expanded: string[], setExpanded: (newExpanded: string[]) => void }) {
    const { visibleMembers } = props;

    return (
      <React.Fragment>
        <TableRow>
          <TableCell style={{ padding: 0 }} colSpan={6}>
              <Box sx={{ my: 1 }}>
                <Table size="medium" aria-label="department members">
                  <TableHead>
                    <TableCell component="th" width={'33px'}>
                      <Checkbox
                        color="success"
                        disableRipple
                        checked={isAllSelected}
                        onClick={selectAll}
                        sx={{
                          pl: 0,
                          ...defaultCheckboxStyles
                        }}
                      />
                    </TableCell>
                    <TableCell className={styles.tableHeader} component="th">

                      <b>{t('securityGroups.headers.name')}</b>
                    </TableCell>
                    <TableCell className={styles.tableHeader} component="th"><b>{t('securityGroups.headers.department')}</b></TableCell>
                    <TableCell className={styles.tableHeader} component="th"><b>{t('securityGroups.headers.position')}</b></TableCell>
                  </TableHead>
                  <TableBody>
                    {visibleMembers.map((member: DisplayMember, index: number) => {
                      const isItemSelected = isSelected(member.personId);
                      return (
                        <TableRow
                          key={member.personId} className={styles.tableRow}
                          sx={{ ...(visibleMembers.length === (index + 1) ? { borderBottom: 'hidden' } : {}) }}
                        >
                          <TableCell scope="row" >
                            <Checkbox
                              color="success"
                              checked={isItemSelected}
                              disableRipple
                              onClick={(event) => handleMemberClick(event, member.personId)}
                              sx={{
                                pl: 0,
                                ...defaultCheckboxStyles
                              }}
                            />
                          </TableCell>
                          <TableCell scope="row" width="33%">
                            {member.firstName} {member.lastName}
                          </TableCell>
                          <TableCell width="33%">{member.departmentName}</TableCell>
                          <TableCell>{member.position}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>

          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const handleMemberClick = (event: React.MouseEvent<HTMLButtonElement>, personId: string) => {
    event.stopPropagation();
    const existingPersonId: string | undefined = selected.find((person: string) => person === personId);
    let newSelected: string[] = [];
    if (existingPersonId) {
      newSelected = selected.filter((person: string) => person !== personId);
    } else {
      newSelected = [...selected, personId];
    }
    setSelected(newSelected);
  }

  return (
    <div className={styles.ManagePermissions} data-testid="SecurityGroups">
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer
            sx={{
              maxHeight: `calc(100vh - ${headerHeight}px - ${ButtonRowHeight} - ${tabHeaderHeight}px - 10px)`,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              marginTop: '15px',
              ...scrollStyles
            }}
          >
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="securityGroups"
              size={'medium'}
              stickyHeader
            >

              <TableBody>
                {(flatMembersList && flatMembersList.length > 0) &&
                  (
                    <GroupedTableRows
                      department={''}
                      visibleMembers={flatMembersList}
                      expanded={expanded}
                      setExpanded={handleSetExpanded}
                    />
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </div>
  )
};

export default GroupsTable;
